import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { LoadingScreen } from "../lib/Loading.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { Spotlight } from "../spotlight/Spotlight.tsx";
import { AttributionServiceCases } from "../verifications/AttributionServiceCases.tsx";
import { BulkActionBar } from "../verifications/BulkActionBar.tsx";
import { VerificationCard } from "../verifications/VerificationCard.tsx";
import type { RequestCard } from "../verifications/verifications.model.ts";
import { AUTHENTICATIONS_COPY } from "./authentications.constants.ts";
import { useAuthenticationsController } from "./useAuthenticationsController.ts";
import "../verifications/verifications.css";
import "./estate.css";

/**
 * Authentication Requests — OND-212. The estate's own queue.
 *
 * THREE SECTIONS, EACH DRAWN BY A COMPONENT THAT ALREADY EXISTS: the inbox's
 * card for a request, the service tab's list for a case, the catalogue's row
 * for a confirmed work. The screen adds headings and one line under each
 * awaiting request; it decides nothing, and every control on it is the same
 * control the Verifications screen offers, refused by the same server.
 */
export function Authentications({
  principalOndoId,
  workBase,
  catalogueHref,
}: {
  principalOndoId: string;
  workBase: string;
  catalogueHref: string;
}) {
  const c = useAuthenticationsController({ principalOndoId, workBase });
  const { view } = c;

  if (view.kind === "loading") return <LoadingScreen message={view.message} />;
  if (view.kind === "error") return <p role="alert">{view.message}</p>;

  const selectedTitles = view.awaiting
    .filter((row) => c.selected.includes(row.requestId))
    .map((row) => `${row.title} — ${row.work.title}`);

  return (
    <div className="verifications estate__authentications">
      <p className="verifications__help">{AUTHENTICATIONS_COPY.lead}</p>

      {view.error && (
        <p role="alert" className="verifications__error">
          {view.error}
        </p>
      )}

      <section className="estate__section" aria-labelledby="auth-awaiting">
        <h2 id="auth-awaiting" className="estate__section-title">
          {AUTHENTICATIONS_COPY.awaitingTitle} · {view.awaiting.length}
        </h2>
        {view.awaiting.length === 0 ? (
          <EmptyState
            className="verifications__empty"
            lead={AUTHENTICATIONS_COPY.awaitingEmpty}
            detail={AUTHENTICATIONS_COPY.awaitingEmptyDetail}
          />
        ) : (
          <>
            <BulkActionBar
              selected={c.selected}
              titles={selectedTitles}
              busy={view.busy}
              onSend={c.answerMany}
              onClear={c.clearSelection}
            />
            <ul className="verifications__list">
              {view.awaiting.map((card) => (
                <RequestWithStrip
                  key={card.requestId}
                  strip={view.strips[card.requestId] ?? null}
                  card={card}
                  c={c}
                  busy={view.busy}
                />
              ))}
            </ul>
          </>
        )}
      </section>

      {view.answered.length > 0 && (
        <details className="estate__section estate__fold">
          <summary className="estate__section-title estate__fold-summary">
            {AUTHENTICATIONS_COPY.answeredTitle} · {view.answered.length}
          </summary>
          <p className="verifications__help">{AUTHENTICATIONS_COPY.answeredDetail}</p>
          <ul className="verifications__list">
            {view.answered.map((card) => (
              <RequestWithStrip key={card.requestId} strip={null} card={card} c={c} busy={view.busy} />
            ))}
          </ul>
        </details>
      )}

      <section className="estate__section" aria-labelledby="auth-service">
        <h2 id="auth-service" className="estate__section-title">
          {AUTHENTICATIONS_COPY.serviceTitle} · {view.cases.length}
        </h2>
        <p className="verifications__help">{AUTHENTICATIONS_COPY.serviceLead}</p>
        {view.cases.length === 0 ? (
          <p className="estate__note">{AUTHENTICATIONS_COPY.serviceEmpty}</p>
        ) : (
          <AttributionServiceCases
            cases={view.cases}
            log={c.caseLog}
            busy={view.busy}
            onOpenLog={c.openCaseLog}
            onAskEvidence={c.askEvidence}
            onExtend={c.extendTarget}
            onAssign={c.assignReviewer}
            onRecordPaid={c.recordFeePaid}
            onCorrect={c.correctFee}
          />
        )}
      </section>

      <section className="estate__section" aria-labelledby="auth-confirmed">
        <h2 id="auth-confirmed" className="estate__section-title">
          {AUTHENTICATIONS_COPY.confirmedTitle}
          {view.confirmed ? ` · ${view.confirmed.length}` : ""}
        </h2>
        <p className="verifications__help">{AUTHENTICATIONS_COPY.confirmedLead}</p>
        {view.confirmed === null ? null : view.confirmed.length === 0 ? (
          <EmptyState
            className="estate__empty"
            lead={AUTHENTICATIONS_COPY.confirmedEmpty}
            detail={AUTHENTICATIONS_COPY.confirmedEmptyDetail}
          />
        ) : (
          <ul className="estate__rows">
            {view.confirmed.map((row) => (
              <li className="estate__row" key={row.workId}>
                <span className="estate__thumb">
                  <WorkThumb imageUrl={c.thumbs.get(row.workId) ?? null} title={row.title} />
                </span>
                <span className="estate__row-body">
                  <Link className="estate__row-title" to={row.href}>
                    {row.title}
                  </Link>
                  <span className="estate__row-meta">{row.meta}</span>
                  <span className="estate__row-attribution">{row.attribution}</span>
                </span>
                <span className="estate__row-end">
                  <span className="estate__cr">{row.cr}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="estate__note">
          <Link className="verifications__link" to={catalogueHref}>
            {AUTHENTICATIONS_COPY.catalogueLink}
          </Link>
        </p>
      </section>

      <Spotlight view={c.spotlight} onClose={c.closeDocument} />
    </div>
  );
}

/** The inbox's own card, with the service's line beneath it. */
function RequestWithStrip({
  card,
  strip,
  c,
  busy,
}: {
  card: RequestCard;
  strip: string | null;
  c: ReturnType<typeof useAuthenticationsController>;
  busy: boolean;
}) {
  return (
    <>
      <VerificationCard
        id={`authentication-${card.requestId}`}
        focused={false}
        card={card}
        capacities={c.capacities}
        imageUrl={card.work.imagePath ? (c.images.get(card.work.imagePath) ?? null) : null}
        selected={c.selected.includes(card.requestId)}
        busy={busy}
        onToggle={() => c.toggle(card.requestId)}
        onAnswer={(body) => c.answer(card.requestId, body)}
        onRefer={(ondoId, note) => c.refer(card.requestId, ondoId, note)}
        onAddDocument={(files) => c.addDocument(card.entryId, files)}
        onOpenDocument={(documentId) => c.openDocument(card.requestId, documentId)}
      />
      {strip && <li className="estate__strip">{strip}</li>}
    </>
  );
}
